/* f52ymm-evaluate.js - stable PDP shell + go/no-go bolt pattern + variant ID + variant notes */
(function () {
  'use strict';

  /* ---------- Small helpers ---------- */

  function isPdp() { return !!document.getElementById('f52ymm-pdp'); }

  function qs(sel) { return document.querySelector(sel); }

  function dbg() {
    if (window.f52ymmDebugLog) {
      console.log.apply(console, arguments);
    }
  }

  /* ---------- Phase 1: get stored vehicle ---------- */

  function getVehicle() {
    try {
      const s = sessionStorage.getItem('f52ymmVehicle');
      if (s) return JSON.parse(s);
    } catch {}

    try {
      const l = localStorage.getItem('f52ymm:selectedVehicle');
      if (l) return JSON.parse(l);
    } catch {}

    const h = document.getElementById('f52ymm-selector');
    if (h && h.dataset && (h.dataset.year || h.dataset.model)) {
      return {
        year: h.dataset.year || '',
        make: h.dataset.make || '',
        model: h.dataset.model || '',
        makeId: h.dataset.makeId ? Number(h.dataset.makeId) : null,
        modelId: h.dataset.modelId ? Number(h.dataset.modelId) : null
      };
    }
    return null;
  }

  function hideVerified() {
    const wrap = qs('#f52ymm-pdp .f52ymm-selector-vehicle-verified');
    if (wrap) wrap.classList.remove('visible');
  }

  /* ---------- PDP variant option filtering ---------- */

  function getFitmentOptionElements() {
    const selectors = document.querySelectorAll('variant-picker .option-selector');
    const out = [];

    selectors.forEach(selector => {
      const optionName = String(selector.dataset.option || '').toLowerCase();
      if (!optionName.includes('bolt pattern')) return;

      selector.querySelectorAll('[data-variant-id]').forEach(option => {
        out.push(option);
      });
    });

    return out;
  }

  function clearVariantOptionFilter() {
    getFitmentOptionElements().forEach(option => {
      option.classList.remove('f52ymm-hidden-product-option');
      option.classList.remove('is-disabled');
      option.removeAttribute('aria-hidden');
      option.removeAttribute('aria-disabled');

      if (option.matches('input[type="radio"]') && option.id) {
        const label = document.querySelector(`label[for="${CSS.escape(option.id)}"]`);
        if (label) {
          label.classList.remove('f52ymm-hidden-product-option');
          label.removeAttribute('aria-hidden');
        }
      }
    });
  }

  function filterVariantOptions(allowedSet) {
    if (!allowedSet || !allowedSet.size) {
      clearVariantOptionFilter();
      return;
    }

    getFitmentOptionElements().forEach(option => {
      const isAllowed = allowedSet.has(String(option.dataset.variantId || ''));
      option.classList.toggle('f52ymm-hidden-product-option', !isAllowed);
      option.classList.toggle('is-disabled', !isAllowed);

      if (isAllowed) {
        option.removeAttribute('aria-hidden');
        option.removeAttribute('aria-disabled');
      } else {
        option.setAttribute('aria-hidden', 'true');
        option.setAttribute('aria-disabled', 'true');
      }

      if (option.matches('input[type="radio"]') && option.id) {
        const label = document.querySelector(`label[for="${CSS.escape(option.id)}"]`);
        if (label) {
          label.classList.toggle('f52ymm-hidden-product-option', !isAllowed);
          if (isAllowed) {
            label.removeAttribute('aria-hidden');
          } else {
            label.setAttribute('aria-hidden', 'true');
          }
        }
      }
    });
  }

  // Fallback if collection PCD is not available
  function fallbackAllowedPcd(vehicle) {
    if (!vehicle) return '';

    const make  = String(vehicle.make  || '').toLowerCase();
    const model = String(vehicle.model || '').toLowerCase();

    // Jeep logic for now
    if (make === 'jeep') {
      if (model.includes('wrangler') || model.includes('gladiator')) {
        return '5x127';
      }
    }

    // Add other makes here later if needed
    return '';
  }

  /* ---------- Phase 2: bolt pattern helpers ---------- */

  // Normalize patterns like 5x5 to 5x127, 6x5.5 to 6x139.7, and strip extra text
  function normBolt(s) {
    if (!s) return '';

    let t = String(s).toLowerCase();

    // Normalize whitespace
    t = t.replace(/\s+/g, ' ');

    // Normalize common inch patterns to metric
    // 5x5 or 5 x 5.0 -> 5x127
    t = t.replace(/\b5\s*x\s*5(?:\.0+)?\b/g, '5x127');

    // 6x5.5 -> 6x139.7
    t = t.replace(/\b6\s*x\s*5\.5\b/g, '6x139.7');

    // Prefer patterns where the second number is 3+ digits
    let m = t.match(/(\d{1,2})\s*x\s*(\d{3}(?:\.\d+)?)/);
    if (!m) {
      // Fallback, allow 2 to 3 digits
      m = t.match(/(\d{1,2})\s*x\s*(\d{2,3}(?:\.\d+)?)/);
      if (!m) return '';
    }

    const lugs = m[1];
    let pcd = m[2].replace(/\.0+$/, '');

    return `${lugs}x${pcd}`;
  }

  // Try hard to read the current variant's bolt pattern from the PDP UI
  function getCurrentBoltPattern() {
    const form = qs('form[action*="/cart/add"]');
    if (!form) return '';

    // 1) Variant select [name="id"] text, if visible
    const idSelect = form.querySelector('[name="id"]');
    if (idSelect && idSelect.tagName === 'SELECT') {
      const opt = idSelect.options[idSelect.selectedIndex];
      if (opt && opt.textContent) {
        const n = normBolt(opt.textContent);
        if (n) return n;
      }
    }

    // 2) Explicit bolt/lug/pcd/pattern options
    const pickedEl =
      form.querySelector('[data-option-name*="bolt" i] [aria-checked="true"]') ||
      form.querySelector('[data-option-name*="lug" i] [aria-checked="true"]') ||
      form.querySelector('[data-option-name*="pcd" i] [aria-checked="true"]') ||
      form.querySelector('[data-option-name*="pattern" i] [aria-checked="true"]') ||
      form.querySelector('select[name*="Bolt" i] option:checked') ||
      form.querySelector('select[name*="Lug" i] option:checked') ||
      form.querySelector('select[name*="PCD" i] option:checked') ||
      form.querySelector('select[name*="Pattern" i] option:checked');

    if (pickedEl && pickedEl.textContent) {
      const n = normBolt(pickedEl.textContent.trim());
      if (n) return n;
    }

    // 3) Scan selects whose labels mention bolt/lug/pcd/pattern
    const selects = form.querySelectorAll('select');
    for (let i = 0; i < selects.length; i++) {
      const sel = selects[i];
      const label =
        form.querySelector(`label[for="${sel.id}"]`) ||
        sel.closest('label');
      const labelText = (label && label.textContent || '').toLowerCase();

      if (labelText.match(/bolt|lug|pcd|pattern/)) {
        const opt = sel.options[sel.selectedIndex];
        if (opt && opt.textContent) {
          const n = normBolt(opt.textContent.trim());
          if (n) return n;
        }
      }
    }

    // 4) Fallback to ShopifyAnalytics variant name
    const idInput = form.querySelector('[name="id"]');
    if (idInput) {
      const id = idInput.value;
      const variants = window?.ShopifyAnalytics?.meta?.product?.variants || [];
      const hit = variants.find(v => String(v?.id) === String(id));
      if (hit && hit.name) {
        const n = normBolt(hit.name);
        if (n) return n;
      }
    }

    // 5) Final safety net, scan any text in the form that looks like "5x127"
    const re = /\d{1,2}\s*x\s*\d{2,3}(?:\.\d+)?/;
    const walker = document.createTreeWalker(form, NodeFilter.SHOW_TEXT, null);
    let node;
    while ((node = walker.nextNode())) {
      const txt = node.textContent || '';
      const m = txt.match(re);
      if (m) {
        const n = normBolt(m[0]);
        if (n) return n;
      }
    }

    return '';
  }

  /* ---------- Variant ID helpers using CloudFront YMM JSON ---------- */

  // Get current variant ID from PDP
  function getCurrentVariantId() {
    const form = qs('form[action*="/cart/add"]');
    if (!form) return null;
    const idInput = form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return null;

    const vid = String(idInput.value);
    console.log('[YMM][QA] Current PDP Variant ID:', vid);
    return vid;
  }

  // Redirect helper to send shopper to same product with a specific variant
  function redirectToVariant(targetId) {
    if (!targetId) return false;

    const url = new URL(window.location.href);
    const tid = String(targetId);

    if (url.searchParams.get('variant') === tid) {
      console.log('[YMM][QA] redirectToVariant: already on variant', tid);
      return false;
    }

    url.searchParams.set('variant', tid);
    console.log('[YMM][QA] redirectToVariant: sending to', url.toString());
    window.location.href = url.toString();
    return true;
  }

  // Load YMM JSON, or use cached version
  async function getYmmRaw() {
    if (window.f52YmmRaw && window.f52YmmRaw.makes) {
      return window.f52YmmRaw;
    }

    const url =
      window.f52ymmYmmJsonUrl ||
      (window.f52ymmOptionsBaseUrl
        ? window.f52ymmOptionsBaseUrl + '/ymm/ymm-options.json'
        : null);

    console.log('[YMM][QA] Loading YMM JSON from:', url);

    if (!url) return null;

    try {
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) {
        console.warn('[YMM][QA] Failed to load YMM JSON:', res.status);
        return null;
      }
      const data = await res.json();
      window.f52YmmRaw = data;
      console.log('[YMM][QA] Loaded YMM JSON. Makes count:', data.makes?.length);
      return data;
    } catch (e) {
      console.warn('[YMM][QA] Error loading YMM JSON:', e);
      return null;
    }
  }

  // String normalization helpers for YMM lookups
  function slugify(str) {
    return String(str || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function findMakeBlockInRaw(raw, make) {
    if (!raw || !Array.isArray(raw.makes)) return null;
    const want = String(make || '').toLowerCase();

    for (let i = 0; i < raw.makes.length; i++) {
      const m = raw.makes[i];
      if (m && String(m.title || '').toLowerCase() === want) {
        console.log('[YMM][QA] Found Make Block:', m.title);
        return m;
      }
    }
    console.warn('[YMM][QA] Make not found in YMM JSON:', make);
    return null;
  }

  function modelIncludesYear(rec, selectedYear) {
    const year = Number(selectedYear);
    if (!Number.isFinite(year)) return true;

    if (Array.isArray(rec?.years) && rec.years.length) {
      return rec.years.some(y => Number(y) === year);
    }

    const first = Number(rec?.first_year || rec?.from_year);
    const last = Number(rec?.last_year || rec?.to_year);
    if (Number.isFinite(first) && year < first) return false;
    if (Number.isFinite(last) && year > last) return false;
    return true;
  }

  function findModelRecordInMake(makeBlock, model, selectedYear, modelId) {
    if (!makeBlock) return null;

    const want = String(model || '').toLowerCase();
    const wantSlug = slugify(want);
    const wantId = modelId == null ? '' : String(modelId);
    const models = Array.isArray(makeBlock.models) ? makeBlock.models : [];

    let idMatch = null;
    let exact = null;
    let slugMatch = null;
    let prefixMatch = null;

    for (let j = 0; j < models.length; j++) {
      const rec = models[j] || {};
      if (!modelIncludesYear(rec, selectedYear)) continue;
      const t = String(rec.title || '');
      const tl = t.toLowerCase();

      if (wantId && String(rec.id) === wantId) {
        idMatch = rec;
        break;
      }
      if (!exact && tl === want) exact = rec;
      if (!slugMatch && slugify(t) === wantSlug) slugMatch = rec;
      if (!prefixMatch && tl.indexOf(want) === 0) prefixMatch = rec;
    }

    const found = idMatch || exact || slugMatch || prefixMatch;

    if (found) {
      console.log('[YMM][QA] Found Model Record:', found.title);
    } else {
      console.warn('[YMM][QA] Model not found in YMM JSON:', model);
    }

    return found;
  }

  // Pull shopify_compatible_variants list from YMM JSON
  async function fetchAllowedVariantsFromYmm(vehicle) {
    if (!vehicle || !vehicle.make || !vehicle.model) return null;

    console.log('[YMM][QA] Vehicle for variant-fitment check:', vehicle);

    const raw = await getYmmRaw();
    if (!raw) return null;

    const mkBlock = findMakeBlockInRaw(raw, vehicle.make);
    const info = findModelRecordInMake(mkBlock, vehicle.model, vehicle.year, vehicle.modelId);
    if (!info) return null;

    const ids = info.shopify_compatible_variants;
    if (!ids || !ids.length) {
      console.warn('[YMM][QA] No variant list present for model.');
      return null;
    }

    console.log('[YMM][QA] Compatible Variant Count:', ids.length);

    const out = new Set();
    for (let i = 0; i < ids.length; i++) out.add(String(ids[i]));

    return out;
  }

  // Get a variant-specific fitment note for a vehicle + variant ID
  async function fetchVariantNote(vehicle, variantId) {
    if (!vehicle || !vehicle.make || !vehicle.model || !variantId) return '';

    const raw = await getYmmRaw();
    if (!raw) return '';

    const mkBlock = findMakeBlockInRaw(raw, vehicle.make);
    const info = findModelRecordInMake(mkBlock, vehicle.model, vehicle.year, vehicle.modelId);
    if (!info || !Array.isArray(info.variant_notes)) return '';

    const vid = String(variantId);
    const arr = info.variant_notes;

    for (let i = 0; i < arr.length; i++) {
      const row = arr[i];
      if (!row) continue;
      if (String(row.shopify_variant_id) === vid && row.note) {
        return String(row.note);
      }
    }

    return '';
  }

  // Pick the preferred allowed variant ID for this product and vehicle
  async function pickPreferredAllowedVariant(vehicle, allowedSet) {
    if (!vehicle || !allowedSet || !allowedSet.size) return null;

    // Limit choices to variants that belong to this product
    const productVariants = window?.ShopifyAnalytics?.meta?.product?.variants || [];
    const productIds = new Set();
    for (let i = 0; i < productVariants.length; i++) {
      const v = productVariants[i];
      if (!v) continue;
      productIds.add(String(v.id));
    }

    const raw = await getYmmRaw();
    if (!raw) return null;

    const mkBlock = findMakeBlockInRaw(raw, vehicle.make);
    const info = findModelRecordInMake(mkBlock, vehicle.model, vehicle.year, vehicle.modelId);
    if (!info) return null;

    function inBoth(idStr) {
      return allowedSet.has(idStr) && productIds.has(idStr);
    }

    // 1) Prefer variants that have notes, but only if they belong to this product
    if (Array.isArray(info.variant_notes)) {
      for (let i = 0; i < info.variant_notes.length; i++) {
        const row = info.variant_notes[i];
        if (!row || !row.shopify_variant_id) continue;
        const idStr = String(row.shopify_variant_id);
        if (inBoth(idStr)) {
          console.log('[YMM][QA] pickPreferredAllowedVariant: picked by note + product', idStr);
          return idStr;
        }
      }
    }

    // 2) Next, use order in shopify_compatible_variants, filtered to this product
    if (Array.isArray(info.shopify_compatible_variants)) {
      for (let i = 0; i < info.shopify_compatible_variants.length; i++) {
        const idStr = String(info.shopify_compatible_variants[i]);
        if (inBoth(idStr)) {
          console.log('[YMM][QA] pickPreferredAllowedVariant: picked by list + product', idStr);
          return idStr;
        }
      }
    }

    // 3) Final fallback, first allowed ID that belongs to this product
    const iter = allowedSet.values();
    let next;
    while (!(next = iter.next()).done) {
      const idStr = String(next.value);
      if (productIds.has(idStr)) {
        console.log('[YMM][QA] pickPreferredAllowedVariant: picked by Set + product', idStr);
        return idStr;
      }
    }

    console.warn('[YMM][QA] pickPreferredAllowedVariant: no allowed IDs for this product');
    return null;
  }

  /* ---------- Vehicle collection path + PCD metafield ---------- */

  // Resolve the selected vehicle collection path using the public YMM feed
  async function resolveVehicleCollectionPath(vehicle) {
    const url = window.f52ymmYmmJsonUrl;
    if (!url) return '';
    try {
      const cacheKey = 'f52ymmYmmJsonCache';
      const cached = localStorage.getItem(cacheKey);
      let data = null;
      if (cached) {
        const obj = JSON.parse(cached);
        if (Date.now() < obj.exp) data = obj.data;
      }
      if (!data) {
        data = await fetch(url, { cache: 'force-cache' }).then(r => r.json());
        localStorage.setItem(
          cacheKey,
          JSON.stringify({ data, exp: Date.now() + 6 * 60 * 60 * 1000 })
        );
      }

      const mk = (data?.makes || []).find(
        m => String(m?.title).toLowerCase() === String(vehicle?.make || '').toLowerCase()
      );
      const md = findModelRecordInMake(
        mk,
        vehicle?.model,
        vehicle?.year,
        vehicle?.modelId
      );
      if (!md) return '';

      if (md.shopify_collection_handle) return '/collections/' + md.shopify_collection_handle;
      if (md.collection_handle) return '/collections/' + md.collection_handle;
      if (md.collection_url) return String(md.collection_url).replace(location.origin, '');

      const first = Number(md.first_year || md.from_year || 0);
      const last  = Number(md.last_year  || md.to_year   || 0);
      const slug = s => String(s || '')
        .toLowerCase()
        .trim()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (first && last) {
        return `/collections/aftermarket-wheels-for-${first}-to-${last}-${slug(vehicle.make)}-${slug(vehicle.model)}`;
      }
      return `/collections/aftermarket-wheels-for-${slug(vehicle.make)}-${slug(vehicle.model)}`;
    } catch {
      return '';
    }
  }

  // Pull allowed PCD from the collection metafield view ?view=pcd.json
  async function fetchAllowedPcd(vehicle) {
    try {
      const cacheKey = 'f52ymmPcdByCollection';

      // Safe cache load
      let cache = {};
      try {
        const raw = localStorage.getItem(cacheKey);
        cache = raw ? JSON.parse(raw) : {};
      } catch (e) {
        cache = {};
      }

      const path = await resolveVehicleCollectionPath(vehicle);
      dbg('[YMM] PCD fetch path', path, vehicle);

      const fb = fallbackAllowedPcd(vehicle);

      // If no path at all, just use fallback
      if (!path) {
        if (fb) dbg('[YMM] fallback PCD with no path', fb);
        return fb || '';
      }

      const handle = path
        .replace(/^\/collections\//, '')
        .replace(/^\/+/, '');

      // Cache hit
      if (cache[handle]) {
        let cached = cache[handle];

        if (fb && cached !== fb) {
          cached = fb;
          cache[handle] = cached;
          try {
            localStorage.setItem(cacheKey, JSON.stringify(cache));
          } catch (e) {}
        }

        dbg('[YMM] cache hit PCD', handle, cached);
        return cached;
      }

      // Try reading from the pcd.json view
      let pcd = '';
      try {
        const res = await fetch(path.replace(/\/$/, '') + '?view=pcd.json', {
          cache: 'no-store'
        });
        dbg('[YMM] PCD response', res.status);
        if (res.ok) {
          const data = await res.json();
          dbg('[YMM] PCD json', data);
          pcd = normBolt(data && data.pcd);
        }
      } catch (e) {
        dbg('[YMM] PCD fetch error', e);
      }

      // If collection gave nothing or something that does not match our Jeep rule,
      // use fallback for now
      if (fb && pcd && pcd !== fb) {
        dbg('[YMM] overriding PCD with fallback', fb);
        pcd = fb;
      }

      if (!pcd) {
        dbg('[YMM] no PCD resolved');
        return '';
      }

      cache[handle] = pcd;
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cache));
      } catch (e) {}

      return pcd;
    } catch (err) {
      dbg('[YMM] fetchAllowedPcd error', err);
      return '';
    }
  }

  /* ---------- State application (shared for variant + PCD) ---------- */

  function applyState(go, vehicle, current, allowed, mode, note) {
    // Top-level PDP warning box
    const warn = qs('#f52ymm-pdp > .product-warning');
    const atc  = qs('form[action*="/cart/add"] [type="submit"]');
    const verifiedBar  = qs('#f52ymm-pdp .f52ymm-selector-vehicle-verified');
    const verifiedName = qs('#f52ymm-pdp .vehicle-verified-name');

    // Variant-specific notes area
    const notesWrap = qs('#f52ymm-pdp .f52ymm-notes');
    const notesWarning = notesWrap ? notesWrap.querySelector('.product-warning') : null;
    const notesText = notesWrap ? notesWrap.querySelector('.f52ymm-notes-text') : null;

    console.log('[YMM][QA] applyState', {
      go,
      mode,
      current,
      allowed,
      note: note || '',
      hasWarn: !!warn,
      hasAtc: !!atc,
      hasNotes: !!notesWrap
    });

    const show = (el, display) => { if (el) el.style.display = display || 'block'; };
    const hide = (el) => { if (el) el.style.display = 'none'; };

    // Reset notes on every run
    if (notesWrap) {
      hide(notesWrap);
      notesWrap.classList.remove('visible');
    }
    if (notesWarning) {
      notesWarning.classList.remove('visible');
    }
    if (notesText) {
      notesText.textContent = '';
    }

    // Unknown or incomplete data → neutral warning, no verified bar, no notes
    if (go === null) {
      console.log('[YMM][QA] applyState → neutral state');

      hide(verifiedBar);
      if (verifiedBar) verifiedBar.classList.remove('visible');
      if (verifiedName) verifiedName.textContent = '';

      if (warn) {
        show(warn);
        warn.classList.add('visible');
        const txt = warn.querySelector('.warning-text');
        if (txt) txt.textContent = 'Compatibility for this option is not confirmed yet.';
      }
      if (atc) atc.classList.remove('f52ymm-atc-button-disabled');
      return;
    }

    // GO (fit confirmed) → green bar, optional note
    if (go) {
      console.log('[YMM][QA] applyState → GO (fit confirmed)');

      // Hide warning
      if (warn) {
        hide(warn);
        warn.classList.remove('visible');
      }

      // Show verified bar
      if (verifiedBar) {
        show(verifiedBar, 'flex');
        verifiedBar.classList.add('visible');
      }
      if (verifiedName) {
        verifiedName.textContent = (
          `Fits your ${vehicle.year || ''} ${vehicle.make ? vehicle.make + ' ' : ''}${vehicle.model || ''}`
        ).trim();
      }

      if (atc) atc.classList.remove('f52ymm-atc-button-disabled');

      // Show variant-specific note if present
      if (note && notesWrap && notesWarning && notesText) {
        notesText.textContent = note;
        show(notesWrap);
        notesWrap.classList.add('visible');
        notesWarning.classList.add('visible');
      }

      return;
    }

    // NO-GO → warning only, no notes
    console.log('[YMM][QA] applyState → NO-GO', { mode });

    hide(verifiedBar);
    if (verifiedBar) verifiedBar.classList.remove('visible');
    if (verifiedName) verifiedName.textContent = '';

    if (warn) {
      show(warn);
      warn.classList.add('visible');
      const txt = warn.querySelector('.warning-text');
      if (txt) {
        if (mode === 'variant') {
          txt.textContent = 'This option will not fit your selected vehicle.';
        } else {
          txt.textContent = `This option will not fit. Your vehicle uses ${allowed}.`;
        }
      }
    }

    if (atc) atc.classList.add('f52ymm-atc-button-disabled');
  }

  let allowedPCD = '';   // cache per page
  let lastVehicleKey = '';
  let autoVariantApplied = false;

  function vehicleKey(v) {
    if (!v) return '';
    return `${v.year || ''}|${v.make || ''}|${v.model || ''}`;
  }

  /* ---------- Main evaluator: Variant ID first, PCD second ---------- */

  async function evaluateGoNoGo(vehicleParam) {
    // Always re-read vehicle if not explicitly provided
    const vehicle = vehicleParam || getVehicle();

    if (!vehicle) {
      console.log('[YMM][QA] No stored vehicle. Hiding PDP state.');
      hideVerified();
      clearVariantOptionFilter();
      allowedPCD = '';
      lastVehicleKey = '';
      autoVariantApplied = false;

      const atc = qs('form[action*="/cart/add"] [type="submit"]');
      if (atc) atc.classList.remove('f52ymm-atc-button-disabled');

      // Neutral state when no vehicle is selected
      const warn = qs('#f52ymm-pdp > .product-warning');
      if (warn) {
        warn.classList.add('visible');
        const txt = warn.querySelector('.warning-text');
        if (txt) txt.textContent = 'Select vehicle to confirm compatibility.';
      }

      return;
    }

    console.log('[YMM][QA] evaluateGoNoGo for vehicle:', vehicle);

    const key = vehicleKey(vehicle);
    if (key !== lastVehicleKey) {
      console.log('[YMM][QA] Vehicle changed. Resetting cached PCD.');
      allowedPCD = '';
      lastVehicleKey = key;
      autoVariantApplied = false;
    }

    /* ---------------------------------------------------
       1) VARIANT ID FITMENT CHECK (PRIMARY)
       --------------------------------------------------- */
    const currentVariantId = getCurrentVariantId();

    if (currentVariantId) {
      const [allowedVariants, variantNote] = await Promise.all([
        fetchAllowedVariantsFromYmm(vehicle),
        fetchVariantNote(vehicle, currentVariantId)
      ]);

      if (allowedVariants && allowedVariants.size) {
        filterVariantOptions(allowedVariants);

        const currentStr = String(currentVariantId);
        const goVariant = allowedVariants.has(currentStr);

        console.log(
          `[YMM][QA] Variant Check → Current Variant: ${currentStr}, Allowed: ${goVariant}`
        );

        // If variant is not allowed, once per load, redirect to preferred allowed variant
        if (!goVariant && !autoVariantApplied) {
          const preferredId = await pickPreferredAllowedVariant(vehicle, allowedVariants);
          autoVariantApplied = true;
          if (preferredId) {
            const redirected = redirectToVariant(preferredId);
            if (redirected) {
              return;
            }
          }
        }

        // Variant-based check with optional per-variant note
        applyState(goVariant, vehicle, currentStr, 'variant-list', 'variant', variantNote);
        return;
      }

      console.log('[YMM][QA] Variant list not found. Falling back to PCD.');
    }

    clearVariantOptionFilter();

    /* ---------------------------------------------------
       2) PCD FALLBACK LOGIC (LEGACY)
       --------------------------------------------------- */
    if (!allowedPCD) {
      console.log('[YMM][QA] Fetching allowed PCD from metafield view.');
      allowedPCD = await fetchAllowedPcd(vehicle);
    }

    if (!allowedPCD) {
      console.warn('[YMM][QA] No PCD available for fallback. Showing neutral state.');
      applyState(null, vehicle, '', '', 'pcd');
      return;
    }

    const current = getCurrentBoltPattern();
    console.log('[YMM][QA] PCD Check: current PCD =', current, 'allowed PCD =', allowedPCD);

    if (!current) {
      console.warn('[YMM][QA] Could not detect current bolt pattern. Neutral state.');
      applyState(null, vehicle, '', allowedPCD, 'pcd');
      return;
    }

    const go = current === allowedPCD;
    applyState(go, vehicle, current, allowedPCD, 'pcd');
  }

  /* ---------- Variant listeners ---------- */

  function wireVariantListeners() {
    // Re-run fitment several times so we win against any delayed DOM updates
    const fire = () => {
      const delays = [0, 300, 900, 1800];
      delays.forEach(ms => {
        setTimeout(() => {
          evaluateGoNoGo(getVehicle());
        }, ms);
      });
    };

    // Theme / app events
    document.addEventListener('variant:change', fire);
    document.addEventListener('product:variant-change', fire);
    document.addEventListener('theme:variant:change', fire);

    // Form changes
    const form = qs('form[action*="/cart/add"]');
    if (form) {
      form.addEventListener('change', fire, true);
      form.addEventListener('input', fire, true);
    }

    // Watch the variant ID input
    const idEl = qs('form[action*="/cart/add"] [name="id"]');
    if (idEl) {
      const obs = new MutationObserver(fire);
      obs.observe(idEl, { attributes: true, attributeFilter: ['value'] });
    }
  }

  /* ---------- Boot ---------- */

  async function init() {
    if (!isPdp()) return;

    const vehicle = getVehicle();
    console.log('[YMM][QA] PDP init. Stored vehicle:', vehicle);

    // Initial state based on current vehicle and variant
    await evaluateGoNoGo(vehicle);

    // wire header events
    document.addEventListener('f52ymm:vehicleSaved', async () => {
      const nv = getVehicle();
      console.log('[YMM][QA] Event: vehicleSaved. New vehicle:', nv);
      await evaluateGoNoGo(nv);
    });
    document.addEventListener('f52ymm:vehicleCleared', async () => {
      console.log('[YMM][QA] Event: vehicleCleared.');
      await evaluateGoNoGo(null);
    });
    document.addEventListener('f52ymm:vehicleRestored', async () => {
      const rv = getVehicle();
      console.log('[YMM][QA] Event: vehicleRestored. Vehicle:', rv);
      await evaluateGoNoGo(rv);
    });

    wireVariantListeners();

    // run again after theme finishes mounting
    setTimeout(() => evaluateGoNoGo(getVehicle()), 400);
    setTimeout(() => evaluateGoNoGo(getVehicle()), 1200);

    // safety, poll once per second so we always recover after any DOM re-render
    setInterval(() => {
      if (isPdp()) {
        evaluateGoNoGo();
      }
    }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // debug helpers
  window.f52ymmDebug = {
    getVehicle,
    resolveVehicleCollectionPath,
    fetchAllowedPcd,
    getCurrentBoltPattern,
    normBolt,
    evaluateGoNoGo
  };

  window.f52ymmEvaluate = () => evaluateGoNoGo(getVehicle());
  window.f52ymmEvalRun = function () {
    console.log('[YMM] manual run');
    window.f52ymmEvaluate();
  };
})();
